/**
 * EstimateChip
 *
 * A proposed story's estimate: value, unit, and a confidence indicator. The
 * chip is a real button so its grounding references — why the number is what
 * it is — are reachable on hover *and* keyboard focus via `ui/Tooltip`.
 */

import { Component, For, Show } from "solid-js";
import { Tooltip } from "../ui/Tooltip";
import { formatEstimate } from "../../utils/estimate";
import type {
  EstimateConfidence,
  EstimateReference,
  ProposedEstimate,
} from "../../api/types";

export interface EstimateChipProps {
  estimate: ProposedEstimate;
  /** Story title, folded into the trigger's accessible name */
  storyTitle: string;
}

const CONFIDENCE_LABELS: Record<EstimateConfidence, string> = {
  high: "high confidence",
  medium: "medium confidence",
  low: "low confidence",
};

const CONFIDENCE_DOT_CLASSES: Record<EstimateConfidence, string> = {
  high: "bg-signal-green",
  medium: "bg-signal-amber",
  low: "bg-ink-3",
};

export const EstimateChip: Component<EstimateChipProps> = (props) => {
  const label = () => formatEstimate(props.estimate.value, props.estimate.unit);
  const confidenceLabel = () => CONFIDENCE_LABELS[props.estimate.confidence];

  return (
    <Tooltip
      wide
      position="top"
      content={<ReferenceList references={props.estimate.references} />}
    >
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-full border border-line bg-sunken px-2.5 py-1 text-xs font-mono font-medium text-ink-2 hover:border-line-strong focus-ring"
        aria-label={`Estimate for "${props.storyTitle}": ${label()}, ${confidenceLabel()}. Show grounding.`}
      >
        <span
          class={
            "h-1.5 w-1.5 rounded-full " +
            CONFIDENCE_DOT_CLASSES[props.estimate.confidence]
          }
          aria-hidden="true"
        />
        {label()}
      </button>
    </Tooltip>
  );
};

const ReferenceList: Component<{ references: EstimateReference[] }> = (
  props,
) => (
  <Show
    when={props.references.length > 0}
    fallback={<p>No grounding references.</p>}
  >
    <ul class="flex flex-col gap-2">
      <For each={props.references}>
        {(reference) => (
          <li>
            <p class="font-medium text-paper">
              <Show when={reference.display_id} fallback={reference.label}>
                {reference.display_id} · {reference.label}
              </Show>
            </p>
            <p class="text-paper/70">{reference.note}</p>
          </li>
        )}
      </For>
    </ul>
  </Show>
);

export default EstimateChip;
